import { useCallback, useEffect, useRef, useState } from 'react';

import { logError, trackEvent } from '@/modules/analytics';
import { postMetricPoint, submitPhqGadResponses } from '@/modules/api';
import { AssessmentContent, type Answers } from '@/modules/dynamicAssessments';
import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { usePhqGadQuestions } from './hooks/usePhqGadQuestions';

const PHQ_2_METRIC = '/api/v3/metric/phq-2/';
const GAD_2_METRIC = '/api/v3/metric/gad-2/';

// Sparta's `phq-gad-screen-click-continue` sends responses as a numeric map.
const numericResponses = (answers: Answers): Record<string, number> =>
  Object.fromEntries(Object.entries(answers).map(([key, value]) => [key, Number(value)]));

/**
 * PHQ-GAD anxiety/depression screener — the last survey. Mirrors Sparta's
 * abbreviated → extended flow:
 *  - Show the abbreviated set (phq/gad-1 & -2). On submit, record the
 *    preliminary phq-2 / gad-2 metric points (so onboarding can resume / skip
 *    later).
 *  - If either preliminary score is elevated (>= 3), show the extended set
 *    (phq-3..8 / gad-3..7); on submit, POST the full responses (the backend
 *    records phq-8 / gad-7 and returns scores).
 *  - Otherwise (screened low) the survey is done — advance to programs.
 *
 * A crisis-line result (requiresCrisisLine + qualifiedProgramUrns) routes to the
 * crisis program screen via enterCrisisProgram (Sparta's setCrisisProgramForOverview);
 * otherwise the survey is done. Any fetch/submit error fails forward to programs.
 */
export function PhqGadScreen() {
  // Leaving phq-gad means the survey is behind the user — call markSurveyComplete
  // and let the DRIVER route into program matching. Entering that region needs the
  // async prescribe-programs catalog to pick the entry screen (full list vs
  // primary-recommendation detail), so we DON'T goToNextStep here: a synchronous
  // hop would land on the list and then flash to the detail once the catalog
  // loaded. markSurveyComplete flips the survey resource, the driver fetches the
  // catalog (the user holds on phq-gad meanwhile), then routes to the right entry.
  const { user, markSurveyComplete, enterCrisisProgram } = useRulesEngine();
  const { abbreviated, extended, isLoading, error } = usePhqGadQuestions();

  const [phase, setPhase] = useState<'abbreviated' | 'extended'>('abbreviated');
  const [abbreviatedAnswers, setAbbreviatedAnswers] = useState<Answers>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const viewedPhaseRef = useRef<'abbreviated' | 'extended' | null>(null);

  const onAbbreviatedContinue = useCallback(
    async (answers: Answers) => {
      const resourceUri = user?.resourceUri;
      if (!resourceUri) {
        markSurveyComplete();
        return;
      }
      trackEvent('phq-gad-screen-click-continue', {
        uuid: user?.uuid,
        isExtendedVersion: false,
        responses: numericResponses(answers),
      });
      const phq2 = Number(answers['phq-1'] ?? 0) + Number(answers['phq-2'] ?? 0);
      const gad2 = Number(answers['gad-1'] ?? 0) + Number(answers['gad-2'] ?? 0);
      // Posted sequentially (matches Sparta), so PHQ-2 can land before GAD-2
      // throws. Track which completed so the fail-forward catch can record the
      // exact partial state — otherwise a "PHQ-2 present, no GAD-2" record is
      // unexplained during incident triage.
      let phq2Posted = false;
      try {
        setIsSubmitting(true);
        await postMetricPoint({ metric: PHQ_2_METRIC, user: resourceUri, value: phq2 });
        phq2Posted = true;
        await postMetricPoint({ metric: GAD_2_METRIC, user: resourceUri, value: gad2 });
        if ((phq2 >= 3 || gad2 >= 3) && extended.length > 0) {
          setAbbreviatedAnswers(answers);
          setPhase('extended');
        } else {
          markSurveyComplete(); // screened low (or no extended questions) → done
        }
      } catch (err) {
        // Fail forward to programs (Sparta parity), but log it — the metric
        // post is otherwise a silent failure.
        logError('phq-gad metric point submit failed', {
          error: err instanceof Error ? err.message : String(err),
          phq2Posted,
          failedMetric: phq2Posted ? GAD_2_METRIC : PHQ_2_METRIC,
        });
        markSurveyComplete();
      } finally {
        setIsSubmitting(false);
      }
    },
    [user?.resourceUri, user?.uuid, extended.length, markSurveyComplete]
  );

  const onExtendedContinue = useCallback(
    async (answers: Answers) => {
      const userUrn = user?.urn;
      if (!userUrn) {
        markSurveyComplete();
        return;
      }
      const merged = { ...abbreviatedAnswers, ...answers };
      trackEvent('phq-gad-screen-click-continue', {
        uuid: user?.uuid,
        isExtendedVersion: true,
        responses: numericResponses(merged),
      });
      try {
        setIsSubmitting(true);
        const result = await submitPhqGadResponses(
          userUrn,
          Object.entries(merged).map(([questionKey, value]) => ({
            questionKey,
            choiceIndex: Number(value),
          }))
        );
        // A crisis-line result routes to the crisis program screen instead of
        // normal program matching (Sparta's setCrisisProgramForOverview); the
        // qualified program is enrolled there. Otherwise the survey is done.
        if (result?.requiresCrisisLine && result.qualifiedProgramUrns.length > 0) {
          enterCrisisProgram(result.qualifiedProgramUrns);
        } else {
          markSurveyComplete();
        }
      } catch (err) {
        // Fail forward to programs (Sparta parity), but log the dropped
        // extended responses for triage rather than swallowing silently.
        logError('phq-gad responses submit failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        markSurveyComplete();
      } finally {
        setIsSubmitting(false);
      }
    },
    [user?.urn, user?.uuid, abbreviatedAnswers, markSurveyComplete, enterCrisisProgram]
  );

  const shouldAdvance = !isLoading && (error != null || abbreviated.length === 0);
  useEffect(() => {
    if (shouldAdvance) markSurveyComplete();
  }, [shouldAdvance, markSurveyComplete]);

  // Fire "viewed" once per phase the screener shows (not while loading or
  // fail-forwarding) — so it fires for the abbreviated set and again when the
  // user moves to the extended set, mirroring Sparta's view event keyed on the
  // extended-questions flag.
  useEffect(() => {
    if (!isLoading && !shouldAdvance && user?.uuid && viewedPhaseRef.current !== phase) {
      viewedPhaseRef.current = phase;
      trackEvent('phq-gad-screen-viewed', {
        uuid: user.uuid,
        isExtendedVersion: phase === 'extended',
      });
    }
  }, [isLoading, shouldAdvance, user?.uuid, phase]);

  if (isLoading || shouldAdvance) {
    return <LoadingScreen />;
  }

  const isExtended = phase === 'extended';

  return (
    <AssessmentContent
      key={phase}
      questions={isExtended ? extended : abbreviated}
      onContinue={isExtended ? onExtendedContinue : onAbbreviatedContinue}
      isSubmitting={isSubmitting}
      testIDPrefix='phq-gad'
    />
  );
}
